import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountsReceivableService } from '../../../../services/accounts-receivable.service';
import { CashflowService } from '../../../../services/cashflow.service';
import { CustomersService } from '../../../../services/customers.service';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { StatusService } from '../../../../services/status.service';
import {
  AccountsReceivable,
  AccountsReceivableCreate,
  AccountsReceivableUpdate
} from '../../../../models/AccountsReceivable';
import {
  buildInstallmentDueDates,
  formatReceivableDateForApi,
  splitAmountIntoInstallments
} from '../../../../utils/accounts-receivable.utils';
import { Customer } from '../../../../models/Customer';
import { CostCenter } from '../../../../models/CostCenter';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-accounts-receivable-form',
  templateUrl: './accounts-receivable-form.component.html',
  styleUrls: ['./accounts-receivable-form.component.css']
})
export class AccountsReceivableFormComponent implements OnInit {
  // Formulários
  formStep1!: FormGroup; // Informações do documento
  formStep2!: FormGroup; // Valores e vencimento
  formStep3!: FormGroup; // Cliente e classificação
  formStep4!: FormGroup; // Detalhes adicionais

  currentStep = 1;
  totalSteps = 3;
  availableTabs = [
    { id: 'step1', label: 'Informações do Documento', icon: 'fas fa-file-alt', required: true, completed: false },
    { id: 'step2', label: 'Valores e Vencimento', icon: 'fas fa-calculator', required: true, completed: false },
    { id: 'step3', label: 'Cliente e Classificação', icon: 'fas fa-user', required: true, completed: false }
  ];
  activeTab = 'step1';
  accountsReceivableId?: number;
  isSubmitting = false;
  isEditMode = false;
  loading = false;
  error: string | null = null;
  success: string | null = null;
  loadingStatuses = false;

  // Dados relacionados
  customers: Customer[] = [];
  costCenters: CostCenter[] = [];
  accountsReceivable: AccountsReceivable | null = null;

  // Opções para selects
  documentTypes = [
    'Nota Fiscal',
    'Fatura',
    'Recibo',
    'Duplicata',
    'Boleto',
    'Contrato',
    'Ordem de Serviço',
    'Locação',
    'Venda',
    'Prestação de Serviços',
    'Consultoria',
    'Outros'
  ];

  categoryOptions = [
    'Vendas',
    'Serviços',
    'Locação',
    'Manutenção',
    'Multas',
    'Juros',
    'Consultoria',
    'Treinamento',
    'Licenças',
    'Assinaturas',
    'Comissões',
    'Bonificações',
    'Reembolsos',
    'Devolução Caução',
    'Outros'
  ];

  statusOptions: Status[] = [];

  /** Intervalo entre vencimentos (dias) — usado no batch. */
  billingIntervalPresets = [
    { value: '7', label: 'A cada 7 dias' },
    { value: '15', label: 'A cada 15 dias' },
    { value: '30', label: 'A cada 30 dias' },
    { value: 'custom', label: 'Personalizado' }
  ];

  /** Formas de recebimento: inclui Asaas e Banco do Brasil para quando o pagamento vier desses gateways */
  receiptMethods = [
    'Dinheiro',
    'Cartão de Débito',
    'Cartão de Crédito',
    'Transferência Bancária',
    'PIX',
    'Boleto',
    'PIX (Asaas)',
    'Boleto (Asaas)',
    'Cartão (Asaas)',
    'PIX (Banco do Brasil)',
    'Boleto (Banco do Brasil)',
    'Cheque',
    'Depósito',
    'Outros'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private accountsReceivableService: AccountsReceivableService,
    private customersService: CustomersService,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private cashflowService: CashflowService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadCustomers();
    this.loadCostCenters();
    this.loadAccountsReceivableStatuses();
    
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.accountsReceivableId = +params['id'];
        this.isEditMode = true;
        this.loadAccountsReceivable();
      }
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    });
  }

  initializeForms(): void {
    this.formStep1 = this.fb.group({
      contractNumber: ['', [Validators.maxLength(50)]],
      ourNumber: ['', [Validators.maxLength(50)]],
      bankReferenceNumber: ['', [Validators.maxLength(100)]],
      documentNumber: ['', [Validators.required, Validators.maxLength(50)]],
      documentType: ['', [Validators.required]],
      issueDate: ['', [Validators.required]],
      invoiceNumber: ['', [Validators.maxLength(100)]],
      saleOrderNumber: ['', [Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]]
    });

    const today = new Date().toISOString().split('T')[0];

    this.formStep2 = this.fb.group({
      originalAmount: [0, [Validators.required, Validators.min(0.01)]],
      dueDate: [today, [Validators.required]],
      discountAmount: [0, [Validators.min(0)]],
      interestAmount: [0, [Validators.min(0)]],
      fineAmount: [0, [Validators.min(0)]],
      totalInstallments: [1, [Validators.min(1), Validators.max(120)]],
      billingIntervalPreset: ['30'],
      billingIntervalCustomDays: [30, [Validators.min(1), Validators.max(365)]]
    });


    this.formStep3 = this.fb.group({
      customerId: [''],
      category: ['', [Validators.required]],
      costCenterId: [''],
      subcategory: ['', [Validators.maxLength(100)]],
      bankName: ['', [Validators.maxLength(100)]],
      receiptType: ['', [Validators.maxLength(100)]],
      status: ['Pendente', [Validators.required]]
    });

    this.formStep4 = this.fb.group({
      discountDays: [null, [Validators.min(0)]],
      isRecurring: [false],
      recurrenceFrequency: [''],
      recurrenceEndDate: [''],
      receiptMethod: [''],
      receiptReference: ['', [Validators.maxLength(100)]],
      bankSlipNumber: ['', [Validators.maxLength(100)]],
      checkNumber: ['', [Validators.maxLength(100)]],
      receiptDate: [''],
      amountReceived: [0, [Validators.min(0)]]
    });
  }

  loadCustomers(): void {
    this.customersService.getAll().subscribe({
      next: (customers: Customer[]) => {
        this.customers = customers || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar clientes:', error);
      }
    });
  }

  loadCostCenters(): void {
    this.costCentersService.getAll().subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar centros de custo:', error);
      }
    });
  }

  loadAccountsReceivable(): void {
    if (!this.accountsReceivableId) return;

    this.loading = true;
    this.accountsReceivableService.getAccountsReceivableById(this.accountsReceivableId).subscribe({
      next: (accountsReceivable) => {
        this.accountsReceivable = accountsReceivable;
        this.populateForm();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar conta a receber:', error);
        this.error = 'Erro ao carregar conta a receber';
        this.loading = false;
      }
    });
  }

  populateForm(): void {
    if (!this.accountsReceivable) return;

    const ar = this.accountsReceivable;

    this.formStep1.patchValue({
      contractNumber: ar.contractNumber,
      ourNumber: ar.ourNumber,
      bankReferenceNumber: ar.bankReferenceNumber,
      documentNumber: ar.documentNumber,
      documentType: ar.documentType,
      issueDate: this.formatDateForInput(ar.issueDate),
      invoiceNumber: ar.invoiceNumber,
      saleOrderNumber: ar.saleOrderNumber,
      description: ar.description
    });

    this.formStep2.patchValue({
      originalAmount: ar.originalAmount,
      dueDate: this.formatDateForInput(ar.dueDate),
      discountAmount: ar.discountAmount || 0,
      interestAmount: ar.interestAmount || 0,
      fineAmount: ar.fineAmount || 0,
      totalInstallments: ar.totalInstallments || 1
    });

    this.formStep3.patchValue({
      customerId: ar.customerId,
      category: ar.category,
      costCenterId: ar.costCenterId,
      subcategory: ar.subcategory,
      bankName: ar.bankName,
      receiptType: ar.receiptType,
      status: ar.status
    });

    this.formStep4.patchValue({
      discountDays: ar.discountDays ?? null,
      isRecurring: ar.isRecurring ?? false,
      recurrenceFrequency: ar.recurrenceFrequency ?? '',
      recurrenceEndDate: ar.recurrenceEndDate ? this.formatDateForInput(ar.recurrenceEndDate) : '',
      receiptMethod: ar.receiptMethod,
      receiptReference: ar.receiptReference,
      bankSlipNumber: ar.bankSlipNumber,
      checkNumber: ar.checkNumber,
      receiptDate: ar.receiptDate ? this.formatDateForInput(ar.receiptDate) : '',
      amountReceived: ar.amountReceived || 0
    });
  }

  formatDateForInput(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toISOString().split('T')[0];
  }

  // Getters para facilitar acesso aos campos
  get documentNumber() { return this.formStep1.get('documentNumber'); }
  get documentType() { return this.formStep1.get('documentType'); }
  get originalAmount() { return this.formStep2.get('originalAmount'); }
  get dueDate() { return this.formStep2.get('dueDate'); }
  get category() { return this.formStep3.get('category'); }
  get status() { return this.formStep3.get('status'); }

  /** Mais de uma parcela → POST /accountsreceivable/batch */
  get isBatchMode(): boolean {
    if (this.isEditMode) return false;
    const total = Number(this.formStep2.get('totalInstallments')?.value) || 1;
    return total > 1;
  }

  get resolvedBillingIntervalDays(): number {
    const preset = this.formStep2.get('billingIntervalPreset')?.value;
    if (preset === 'custom') {
      return Math.max(1, Number(this.formStep2.get('billingIntervalCustomDays')?.value) || 1);
    }
    return Math.max(1, Number(preset) || 30);
  }

  get installmentPreview(): { installmentNumber: number; dueDate: string; amount: number }[] {
    if (!this.isBatchMode) return [];
    const count = Number(this.formStep2.get('totalInstallments')?.value) || 0;
    const firstDue = this.formStep2.get('dueDate')?.value;
    if (!firstDue || count < 2) return [];

    const amounts = this.getInstallmentAmountsSplit();
    const dates = buildInstallmentDueDates(firstDue, count, this.resolvedBillingIntervalDays);
    return dates.map((dueDate, index) => ({
      installmentNumber: index + 1,
      dueDate,
      amount: amounts[index] ?? 0
    }));
  }

  /** Valor total informado no formulário (antes de desconto/juros/multa). */
  getTotalAmount(): number {
    return Number(this.formStep2.get('originalAmount')?.value) || 0;
  }

  /** Total líquido (valor total ± desconto, juros, multa). */
  getNetTotalAmount(): number {
    const discountAmount = Number(this.formStep2.get('discountAmount')?.value) || 0;
    const interestAmount = Number(this.formStep2.get('interestAmount')?.value) || 0;
    const fineAmount = Number(this.formStep2.get('fineAmount')?.value) || 0;
    return this.getTotalAmount() - discountAmount + interestAmount + fineAmount;
  }

  /** Valor de cada parcela no lote = total líquido ÷ quantidade de parcelas. */
  getInstallmentAmount(): number {
    const count = Number(this.formStep2.get('totalInstallments')?.value) || 1;
    if (!this.isBatchMode || count < 2) {
      return this.getNetTotalAmount();
    }
    const split = this.getInstallmentAmountsSplit();
    return split[0] ?? 0;
  }

  getInstallmentAmountsSplit(): number[] {
    const count = Number(this.formStep2.get('totalInstallments')?.value) || 1;
    return splitAmountIntoInstallments(this.getNetTotalAmount(), Math.max(1, count));
  }

  isBillingIntervalCustom(): boolean {
    return this.formStep2.get('billingIntervalPreset')?.value === 'custom';
  }

  /** Status é automatizado: exibe Pendente (nova conta) ou o status atual (edição), atualizado só pelo fluxo de pagamento */
  getStatusDisplayValue(): string {
    if (this.isEditMode && this.accountsReceivable?.status) {
      return this.accountsReceivable.status;
    }
    return this.formStep3?.get('status')?.value ?? 'Pendente';
  }

  // Navegação entre steps
  nextStep(): void {
    if (this.canProceedToNextStep()) {
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.formStep1.valid;
      case 2:
        if (!this.formStep2.valid) return false;
        if (this.isBatchMode) {
          const count = Number(this.formStep2.get('totalInstallments')?.value) || 0;
          return this.getNetTotalAmount() >= 0.01 && count >= 2;
        }
        return true;
      case 3:
        return this.formStep3.valid;
      case 4:
        return this.formStep4.valid;
      default:
        return false;
    }
  }

  isFormValid(): boolean {
    if (!(this.formStep1.valid && this.formStep2.valid && this.formStep3.valid && this.formStep4.valid)) {
      return false;
    }
    if (this.isBatchMode) {
      const count = Number(this.formStep2.get('totalInstallments')?.value) || 0;
      return this.getNetTotalAmount() >= 0.01 && count >= 2;
    }
    return true;
  }

  // Validação de campos
  isFieldInvalid(fieldName: string, stepNumber: number): boolean {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string, stepNumber: number): string {
    const form = this.getFormByStep(stepNumber);
    const field = form.get(fieldName);
    
    if (field?.errors) {
      if (field.errors['required']) return 'Este campo é obrigatório';
      if (field.errors['maxlength']) return `Máximo ${field.errors['maxlength'].requiredLength} caracteres`;
      if (field.errors['min']) return `Valor mínimo: ${field.errors['min'].min}`;
      if (field.errors['email']) return 'Email inválido';
    }
    return '';
  }

  getFormByStep(stepNumber: number): FormGroup {
    switch (stepNumber) {
      case 1: return this.formStep1;
      case 2: return this.formStep2;
      case 3: return this.formStep3;
      case 4: return this.formStep4;
      default: return this.formStep1;
    }
  }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= this.totalSteps) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }

  /** Valor total a receber (uma parcela ou soma do lote). */
  calculateTotalAmount(): number {
    return this.getNetTotalAmount();
  }

  // Submissão do formulário
  submitForm(): void {
    if (!this.isFormValid() || this.isSubmitting) return;

    this.isSubmitting = true;
    this.error = null;

    const onSuccess = (): void => {
      this.cashflowService.requestRefresh();
      this.router.navigate(['/financial/accounts-receivable']);
    };
    const onError = (error: { message?: string }): void => {
      console.error('Erro ao salvar conta a receber:', error);
      this.error = error?.message || 'Erro ao salvar conta a receber';
      this.isSubmitting = false;
    };

    if (this.isEditMode) {
      this.accountsReceivableService
        .updateAccountsReceivable(this.prepareFormData() as AccountsReceivableUpdate)
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    if (this.isBatchMode) {
      this.accountsReceivableService
        .createAccountsReceivableBatch(this.buildBatchPayload())
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    this.accountsReceivableService
      .createAccountsReceivable(this.prepareFormData() as AccountsReceivableCreate)
      .subscribe({ next: onSuccess, error: onError });
  }

  prepareFormData(): AccountsReceivableCreate | AccountsReceivableUpdate {
    const step1Data = this.formStep1.value;
    const step2Data = this.formStep2.value;
    const step3Data = this.formStep3.value;
    const step4Data = this.formStep4.value;

    // Helper function to convert to number or undefined
    const toNumberOrUndefined = (value: any): number | undefined => {
      if (value === null || value === undefined || value === '' || value === 'null') {
        return undefined;
      }
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    };

    const baseData = {
      contractNumber: step1Data.contractNumber || undefined,
      ourNumber: step1Data.ourNumber || undefined,
      bankReferenceNumber: step1Data.bankReferenceNumber || undefined,
      documentNumber: step1Data.documentNumber,
      documentType: step1Data.documentType,
      issueDate: step1Data.issueDate,
      dueDate: step2Data.dueDate,
      originalAmount: Number(step2Data.originalAmount),
      discountAmount: toNumberOrUndefined(step2Data.discountAmount),
      interestAmount: toNumberOrUndefined(step2Data.interestAmount),
      fineAmount: toNumberOrUndefined(step2Data.fineAmount),
      amountToReceive: this.calculateTotalAmount(),
      status: this.isEditMode && this.accountsReceivable ? this.accountsReceivable.status : (step3Data.status || 'Pendente'),
      category: step3Data.category,
      subcategory: step3Data.subcategory || undefined,
      bankName: step3Data.bankName || undefined,
      discountDays: toNumberOrUndefined(step4Data.discountDays),
      receiptType: step3Data.receiptType || undefined,
      isRecurring: !!step4Data.isRecurring,
      recurrenceFrequency: step4Data.isRecurring ? (step4Data.recurrenceFrequency || undefined) : undefined,
      recurrenceEndDate: step4Data.isRecurring ? (step4Data.recurrenceEndDate || undefined) : undefined,
      customerId: toNumberOrUndefined(step3Data.customerId),
      costCenterId: toNumberOrUndefined(step3Data.costCenterId),
      description: step1Data.description || undefined,
      invoiceNumber: step1Data.invoiceNumber || undefined,
      saleOrderNumber: step1Data.saleOrderNumber || undefined,
      installmentNumber:
        this.isEditMode && this.accountsReceivable?.installmentNumber != null
          ? this.accountsReceivable.installmentNumber
          : 1,
      totalInstallments: toNumberOrUndefined(step2Data.totalInstallments) ?? 1,
      receiptMethod: step4Data.receiptMethod || undefined,
      receiptReference: step4Data.receiptReference || undefined,
      bankSlipNumber: step4Data.bankSlipNumber || undefined,
      checkNumber: step4Data.checkNumber || undefined,
      receiptDate: step4Data.receiptDate || undefined,
      amountReceived: toNumberOrUndefined(step4Data.amountReceived)
    };

    if (this.isEditMode && this.accountsReceivableId) {
      return {
        id: this.accountsReceivableId,
        ...baseData
      } as AccountsReceivableUpdate;
    }

    return baseData as AccountsReceivableCreate;
  }

  /** Monta N títulos para POST /accountsreceivable/batch com vencimentos espaçados. */
  private buildBatchPayload(): AccountsReceivableCreate[] {
    const base = this.prepareFormData() as AccountsReceivableCreate;
    const step2 = this.formStep2.value;
    const total = Math.max(2, Number(step2.totalInstallments) || 2);
    const firstDue = step2.dueDate;
    const intervalDays = this.resolvedBillingIntervalDays;
    const installmentAmounts = this.getInstallmentAmountsSplit();
    const dueDates = buildInstallmentDueDates(firstDue, total, intervalDays);
    const baseDoc = (base.documentNumber || 'CR').trim();
    const freqLabel = `${intervalDays} dias`;

    return dueDates.map((dueDate, index) => {
      const n = index + 1;
      const suffix = total > 1 ? `/${n}` : '';
      const perInstallment = installmentAmounts[index] ?? 0;
      return {
        ...base,
        documentNumber: `${baseDoc}${suffix}`,
        dueDate,
        issueDate: base.issueDate || formatReceivableDateForApi(new Date()),
        originalAmount: perInstallment,
        amountToReceive: perInstallment,
        discountAmount: index === 0 ? base.discountAmount : 0,
        interestAmount: index === 0 ? base.interestAmount : 0,
        fineAmount: index === 0 ? base.fineAmount : 0,
        installmentNumber: n,
        totalInstallments: total,
        isRecurring: true,
        recurrenceFrequency: freqLabel,
        description: base.description
          ? `${base.description} — Parcela ${n}/${total}`
          : `Parcela ${n}/${total}`
      };
    });
  }

  loadAccountsReceivableStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('AccountsReceivable', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de contas a receber:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Conta pendente', entityType: 'AccountsReceivable', isActive: true },
            { id: 2, name: 'Aprovado', description: 'Conta aprovada', entityType: 'AccountsReceivable', isActive: true },
            { id: 3, name: 'Recebido', description: 'Conta recebida', entityType: 'AccountsReceivable', isActive: true },
            { id: 4, name: 'Cancelado', description: 'Conta cancelada', entityType: 'AccountsReceivable', isActive: true },
            { id: 5, name: 'Vencido', description: 'Conta vencida', entityType: 'AccountsReceivable', isActive: true },
            { id: 6, name: 'Parcial', description: 'Conta parcialmente recebida', entityType: 'AccountsReceivable', isActive: true }
          ] as Status[];
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/financial/accounts-receivable']);
  }
}
